/*
 *** Usage in FO: OK
 *** Usage in PO: OK
 *** Usage in BO: NOT OK - Need some design adaptation - See designers + lead FE devs
 */

import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { AutoSizer, InfiniteLoader, List } from 'react-virtualized';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';

import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import UserRow from './UserRow';

const MAX_ITEMS_BEFORE_SWITCH_TO_VIRTUALIZED_LIST = 15;
const MODAL_HEIGHT = 400;

const UsersModal = ({
  anchorName,
  isFocusOnRef = false,
  onCloseModal,
  responseCallback,
  routes,
  screenReadersCloseButtonText,
  title,
  userNameFormat = undefined,
}) => {
  const [totalUsers, setTotalUsers] = useState(Infinity);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);

  const modalRef = useRef();

  const hasNextPage = useMemo(
    () => totalUsers > users.length,
    [totalUsers, users],
  );

  const maxListHeight = useMemo(
    () =>
      modalRef.current ? modalRef.current.getMaxModalHeight() : MODAL_HEIGHT,
    [modalRef.current],
  );

  const loadNextPage = useCallback(() => {
    setIsNextPageLoading(true);

    axios
      .get(routes.fetchUsers, { params: { per_page: 20, page } })
      .then((response) => {
        let data;
        let usersTemp;
        let total;

        if (responseCallback) {
          ({ users: data, total } = responseCallback(response.data));
          usersTemp = [...users, ...data];
        } else {
          data = response.data[anchorName || 'users'] || [];
          usersTemp = [...users, ...data];
          // NOTE: `response.data?._metadata?.total_items` was added for Journey BO
          // First part of the condition was kept in order to avoid side effects.
          // The first part can be removed if backend can ensure `total_items` is always under `_metadata`
          total =
            response.data?.total_items || response.data?._metadata?.total_items;
        }

        setUsers(usersTemp);
        setTotalUsers(total);
        setIsNextPageLoading(false);
        setPage(page + 1);
      })
      .catch(() => {
        displayNotif('error', __('Sorry, an error occurred'));
        onCloseModal();
      })
      .then(() => {
        setIsNextPageLoading(false);
      });
  }, [page, users, routes]);

  useEffect(() => {
    loadNextPage(users);
  }, []);

  // If there are more items to be loaded then add an extra row to hold a loading indicator.
  const rowCount = hasNextPage ? users.length + 1 : users.length;

  // Only load 1 page of items at a time.
  // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
  const loadMoreRows = isNextPageLoading ? () => {} : loadNextPage;

  // Every row is loaded except for the loading indicator row.
  const isRowLoaded = ({ index }) => !hasNextPage || index < users.length;

  const rowRenderer = ({ index, key, style }) => {
    const user = users[index];

    if (!isRowLoaded({ index })) {
      return (
        <Box justifyContent="center" key={key} mb={4} style={style}>
          <Loader size={40} />
        </Box>
      );
    }

    return (
      <Box display="block" key={key} mb={4} role="row" style={style}>
        <UserRow user={user} userNameFormat={userNameFormat} />
      </Box>
    );
  };

  return (
    <ModalWithTemplate
      customHeight={maxListHeight}
      isFocusOnRef={isFocusOnRef}
      header={title}
      isLoading={isEmpty(users)}
      onCloseModal={onCloseModal}
      pb={isEmpty(users) ? undefined : 0}
      pr={isEmpty(users) ? undefined : 0}
      ref={modalRef}
      screenReadersCloseButtonText={screenReadersCloseButtonText}
      width="small"
    >
      {users.length < MAX_ITEMS_BEFORE_SWITCH_TO_VIRTUALIZED_LIST ? (
        <Box flexDirection="column" minWidth="100%">
          <Box display="block">
            {users.map((user, index) => rowRenderer({ index, key: user.id }))}
          </Box>
        </Box>
      ) : (
        <Box display="block" minHeight={maxListHeight + 1} minWidth="100%">
          <InfiniteLoader
            isRowLoaded={isRowLoaded}
            loadMoreRows={loadMoreRows}
            rowCount={totalUsers}
            threshold={10}
          >
            {({ onRowsRendered, registerChild }) => (
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    onRowsRendered={onRowsRendered}
                    overscanRowCount={10}
                    ref={registerChild}
                    rowCount={rowCount}
                    rowHeight={76}
                    rowRenderer={rowRenderer}
                    width={width}
                    tabIndex={null}
                  />
                )}
              </AutoSizer>
            )}
          </InfiniteLoader>
        </Box>
      )}
    </ModalWithTemplate>
  );
};

UsersModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  routes: PropTypes.shape({
    fetchUsers: PropTypes.string.isRequired,
  }).isRequired,
  title: PropTypes.string.isRequired,
  isFocusOnRef: PropTypes.bool,
  anchorName: PropTypes.string,
  responseCallback: PropTypes.func,
  screenReadersCloseButtonText: PropTypes.string,
};

export default UsersModal;
